/**
 * Elevator Initial Survey layout (from the "Elevator Initial Survey" document).
 * Shared between the public survey form (src/pages/ElevatorSurvey.tsx) and the
 * internal survey detail page so labels/order stay in sync. Values are stored in
 * elevator_initial_surveys.form_data keyed by field id.
 */

export type SurveyFieldType = 'text' | 'date' | 'number' | 'yesno';

export interface SurveyField {
  id: string;
  label: string;
  type: SurveyFieldType;
}

export interface SurveySection {
  title: string;
  fields: SurveyField[];
}

export const ELEVATOR_SURVEY_FORM: SurveySection[] = [
  {
    title: 'Site Information',
    fields: [
      { id: 'customer_property_name', label: 'Customer / Property Name', type: 'text' },
      { id: 'property_address', label: 'Property Address', type: 'text' },
      { id: 'building_name', label: 'Building Name', type: 'text' },
      { id: 'survey_date', label: 'Survey Date', type: 'date' },
      { id: 'surveyed_by', label: 'Surveyed By', type: 'text' },
    ],
  },
  {
    title: 'Elevator Information',
    fields: [
      { id: 'elevator_brand', label: 'Elevator Brand / Manufacturer', type: 'text' },
      { id: 'elevator_model', label: 'Elevator Model', type: 'text' },
      { id: 'elevator_serial', label: 'Elevator Serial Number', type: 'text' },
      { id: 'controller_model', label: 'Elevator Controller Model', type: 'text' },
      { id: 'number_of_elevators', label: 'Number of Elevators', type: 'number' },
      {
        id: 'elevator_for_robotics',
        label: 'Elevator to be utilized for Robotics (location / name / number)',
        type: 'text',
      },
      { id: 'number_of_floors', label: 'Number of Floors Served', type: 'number' },
      { id: 'passenger_or_service', label: 'Passenger or Service Elevator', type: 'text' },
      { id: 'installation_year', label: 'Elevator Installation Year', type: 'text' },
      { id: 'modernization_date', label: 'Elevator Modernization Date (if applicable)', type: 'text' },
    ],
  },
  {
    title: 'Elevator Service Provider',
    fields: [
      { id: 'current_service_company', label: 'Current Elevator Service Company', type: 'text' },
      { id: 'service_contact_name', label: 'Service Contact Name', type: 'text' },
      { id: 'service_phone', label: 'Phone Number', type: 'text' },
      { id: 'service_email', label: 'Email Address', type: 'text' },
      { id: 'service_contract_active', label: 'Service Contract Active?', type: 'yesno' },
    ],
  },
];

/** Flat list of all survey fields (id + label), in document order. */
export const ELEVATOR_SURVEY_FIELDS: SurveyField[] = ELEVATOR_SURVEY_FORM.flatMap((s) => s.fields);

export interface SurveyPhotoField {
  id: string;
  label: string;
}

/**
 * Photos the client uploads with the survey. Stored as rows in
 * elevator_survey_attachments keyed by these ids, one file per id.
 */
export const ELEVATOR_SURVEY_PHOTOS: SurveyPhotoField[] = [
  { id: 'photo_electric_panel', label: 'Photo 1 — Electric Panel' },
  { id: 'photo_elevator_panel', label: 'Photo 2 — Elevator Panel' },
  { id: 'photo_elevator_room', label: 'Photo 3 — Elevator Room (whole area)' },
  { id: 'photo_exterior_doors', label: 'Photo 4 — Exterior Doors and Call Buttons' },
  { id: 'photo_connection_wires', label: 'Photo 5 — Elevator Connection Wires / Signals' },
  { id: 'photo_inside_car', label: 'Photo 6 — Inside Elevator Car' },
];

export const ELEVATOR_SURVEY_PHOTO_IDS: string[] = ELEVATOR_SURVEY_PHOTOS.map((p) => p.id);

export function elevatorSurveyPhotoLabel(fieldId: string): string {
  return ELEVATOR_SURVEY_PHOTOS.find((p) => p.id === fieldId)?.label ?? fieldId;
}
